import { CustomizableLineChart } from '@dealstack/frontend/shared/components/charts/CustomizableLineChart/CustomizableLineChart';
import { InfoBox } from '@dealstack/frontend/shared/components/InfoBox/InfoBox';
import { xTicks } from '@dealstack/frontend/shared/components/VestingChart/VestingChart.helper';
import { generateVestingSummaryMessage } from '@dealstack/frontend/shared/components/VestingTerms/helpers/vesting-messages';
import {
	useTranslationCommonUi,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { formatNumber } from '@dealstack/shared/lib/format/number';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { DATE_FORMATS } from '@dealstack/shared/lib/format/dates/date.ts';
import { extractDecimalPrecisionFromUnitDenominator } from '@dealstack/shared/lib/format/number.ts';
import {
	GetValuationsRes,
	VestingChartDataRes,
	VestingTermSummaryRes,
	VestingTermTimeUnitType,
} from '@dealstack/shared/lib/generated';
import { DateTime } from 'luxon';
import styled from 'styled-components';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';
import { xDomain, yDomain, yTicksPercent } from './VestingChart.helper';

interface Props {
	chartData: VestingChartDataRes[];
	vestingTerms?: VestingTermSummaryRes[];
	earliestTsaDate: GetValuationsRes['earliestTsaDate'];
	tFunction: TranslateFunction<
		I18Namespaces.CAP_MANAGE | I18Namespaces.INVESTOR | I18Namespaces.MIP_MANAGE
	>;
	tooltipTranslationKey: string;
	xLabel: string;
	yLabel: string;
}

const CELL_WIDTH = 64;
const CELL_HEIGHT = 32;

export const VestingChart = ({
	vestingTerms,
	chartData,
	tFunction,
	tooltipTranslationKey,
	xLabel,
	yLabel,
	earliestTsaDate,
}: Props) => {
	const totalWidth = chartData.length * CELL_WIDTH;
	const totalHeight = yTicksPercent.length * CELL_HEIGHT + 40; // chart margin sum needs to be included so labels are not cut off

	if (chartData?.length === 0) {
		return null;
	}

	const hasOnlyOneTerm = vestingTerms?.length === 1;
	const timePeriodUnit =
		hasOnlyOneTerm &&
		vestingTerms[0].vestingTermStraightLine?.periodTimeUnit === VestingTermTimeUnitType.YEARLY
			? VestingTermTimeUnitType.YEARLY
			: VestingTermTimeUnitType.MONTHLY;
	const referenceLine = earliestTsaDate
		? {
				label: DateTime.fromISO(earliestTsaDate).toFormat(DATE_FORMATS.MONTH_DAY_YEAR),
				value: DateTime.fromISO(earliestTsaDate).toMillis(),
			}
		: undefined;
	const firstEventDate = chartData[0]?.date;
	const hideReferenceLine = firstEventDate === referenceLine?.value;

	return (
		<>
			{hasOnlyOneTerm && (
				<VestingTermInfoBox
					vestingTerm={vestingTerms[0]}
					startDate={new Date(firstEventDate)}
					txDate={earliestTsaDate ? new Date(earliestTsaDate) : undefined}
				></VestingTermInfoBox>
			)}
			<StyledChartContainer>
				<CustomizableLineChart<VestingChartDataRes>
					width={totalWidth}
					height={totalHeight}
					data={chartData}
					referenceLine={referenceLine}
					hideReferenceLine={hideReferenceLine}
					xDomain={xDomain}
					dataKey="percentage"
					xTicks={xTicks(chartData, timePeriodUnit)}
					xTicksFormatter={(date: number) =>
						DateTime.fromMillis(date).toFormat('MMM, yyyy')
					}
					xKey="date"
					xLabel={xLabel}
					yTicks={yTicksPercent}
					yDomain={yDomain}
					yLabel={yLabel}
					tooltip={tooltip(tFunction, tooltipTranslationKey)}
				/>
			</StyledChartContainer>
		</>
	);
};

const VestingTermInfoBox = ({
	vestingTerm,
	startDate,
	txDate,
}: {
	vestingTerm?: VestingTermSummaryRes;
	startDate: Date;
	txDate?: Date;
}) => {
	const tMipManage = useTranslationMipManage();
	const tCommonUi = useTranslationCommonUi();

	const infoBoxTitle = vestingTerm?.vestingTermGraded?.length
		? tMipManage('investors.details.holdings_table.chart_info_box.non_uniform_title')
		: tMipManage('investors.details.holdings_table.chart_info_box.uniform_title');
	const infoBoxDescription = generateVestingSummaryMessage(
		tCommonUi,
		vestingTerm,
		startDate,
		txDate
	);

	return (
		<InfoBox
			title={infoBoxTitle}
			description={infoBoxDescription}
			iconVariant="information"
		></InfoBox>
	);
};

const tooltip =
	(tFunction: Props['tFunction'], translationKey: Props['tooltipTranslationKey']) =>
	({ active, payload, label }) => {
		if (active && payload && payload.length) {
			const { percentage, units, unitDenominator } = payload[0].payload;

			return (
				<StyledTooltip>
					{tFunction(translationKey, {
						units: formatNumber({
							value: units,
							lockPrecision: true,
							minPrecision:
								extractDecimalPrecisionFromUnitDenominator(unitDenominator),
							returnNumericalZero: true,
						}),
						percent: percentage,
						formattedDate: DateTime.fromMillis(label).toFormat('DD'),
					})}
				</StyledTooltip>
			);
		}
	};

const StyledTooltip = styled.div`
	border: 1px solid ${({ theme }) => theme.v3.colors.gray[4]};
	box-shadow: 0px 20px 24px 4px #10182814;
	background-color: ${({ theme }) => theme.v3.colors.gray[0]};
	width: 157px;
	border-radius: 0.5rem;
	padding: 1rem;
`;

const StyledChartContainer = styled.div`
	overflow-x: scroll;
	overflow-y: hidden;

	::-webkit-scrollbar-thumb {
		height: 2em;
		background-color: ${({ theme }) => theme.v3.colors.gray[4]};
	}

	tspan {
		fill: ${({ theme }) => theme.v3.colors.dark[4]};
	}
`;
